import { useState, useEffect, useRef, useCallback } from "react";
import "./Loader.css";
import { Nickname } from "../../components";
import { useNavigate } from "react-router-dom";
import officialLogo from "../../assets/logo/the-first-noncarbon-artist.png";

const LOADER_LINES_OF_TEXT = ["THE", "FIRST", "NONCARBON", "ARTIST"];

const Loader = ({
  onComplete,
  isQrCodePage = false,
  onNicknameSubmit = null,
  sessionId,
  isClaimMode = false,
}) => {
  const [positions, setPositions] = useState([]);
  const [textLines, setTextLines] = useState([]);
  const [animationPhase, setAnimationPhase] = useState("rectangle");
  const [animationDone, setAnimationDone] = useState(false);
  const [showNickname, setShowNickname] = useState(false);

  const [nickname, setNickname] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const pixelSize = 6;
  const rectWidth = 30;
  const rectHeight = 20;
  const totalWidth = rectWidth * pixelSize;
  const totalHeight = rectHeight * pixelSize;

  // Use refs to avoid dependency issues
  const onCompleteRef = useRef(onComplete);
  const onNicknameSubmitRef = useRef(onNicknameSubmit);
  
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onNicknameSubmitRef.current = onNicknameSubmit;
  }, [onComplete, onNicknameSubmit]);

  const handleNicknameSubmit = useCallback((nickname) => {
    if (onNicknameSubmitRef.current) {
      onNicknameSubmitRef.current(nickname);
    }
    setAnimationPhase("complete");
  }, []);

  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    if (!nickname.trim()) {
      setError("Please enter a nickname");
      return;
    }
    if (nickname.trim().length > 50) {
      setError("Nickname must be 50 characters or less");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8888";
      const formData = new FormData();
      formData.append("nickname", nickname.trim());
      const response = await fetch(`${apiUrl}/api/session/claim/${sessionId}`, {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (result.success) {
        if (onNicknameSubmitRef.current) {
          onNicknameSubmitRef.current(nickname.trim());
        }
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
        navigate(`/qrcode/${result.session_id}`);
      } else {
        setError(result.error || "Failed to claim session");
        setIsSubmitting(false);
      }
    } catch (err) {
      setError("Network error. Please try again.");
      setIsSubmitting(false);
    }
  };

  const getTextPosition = (index) => {
    const verticalSpacing = 4;
    const topMargin = 3;
    return {
      width: `${(rectWidth - 6) * pixelSize}px`,
      left: `${3 * pixelSize}px`,
      top: `${(index * verticalSpacing + topMargin) * pixelSize}px`,
      fontSize: `${pixelSize * 2.8}px`,
    };
  };

  // Rectangle animation - only runs once on mount
  useEffect(() => {
    const startX = -Math.floor(rectWidth / 2);
    const startY = -Math.floor(rectHeight / 2);
    let rectangle = [];
    let currentPos = { x: startX, y: startY };
    let step = 0;
    let maxSteps = 2 * (rectWidth + rectHeight) - 4;

    const interval = setInterval(() => {
      rectangle.push({ ...currentPos });
      if (step < rectWidth - 1) {
        currentPos.x += 1;
      } else if (step < rectWidth + rectHeight - 2) {
        currentPos.y += 1;
      } else if (step < 2 * rectWidth + rectHeight - 3) {
        currentPos.x -= 1;
      } else if (step < 2 * rectWidth + 2 * rectHeight - 4) {
        currentPos.y -= 1;
      }
      step++;
      setPositions([...rectangle]);
      if (step >= maxSteps) {
      clearInterval(interval);
      setAnimationPhase("text");
      }
    }, 12);

    return () => clearInterval(interval);
  }, []); // Empty deps - only run once

  // Text animation - runs when phase changes to "text"
  useEffect(() => {
    if (animationPhase !== "text") return;
    
    let currentLines = [];
    let lineIndex = 0;
    const textInterval = setInterval(() => {
      if (lineIndex < LOADER_LINES_OF_TEXT.length) {
        currentLines.push(LOADER_LINES_OF_TEXT[lineIndex]);
        setTextLines([...currentLines]);
        lineIndex++;
        if (lineIndex === LOADER_LINES_OF_TEXT.length) {
          clearInterval(textInterval);
          if (isQrCodePage && onNicknameSubmitRef.current) {
            setTimeout(() => setShowNickname(true), 600);
          } else {
            setTimeout(() => setAnimationPhase("complete"), 600);
          }
        }
      } else {
        clearInterval(textInterval);
      }
    }, 400);

    return () => clearInterval(textInterval);
  }, [animationPhase, isQrCodePage]); // Only re-run if these change

  // Complete animation
  useEffect(() => {
    if (animationPhase !== "complete") return;
    setAnimationDone(true);
    const timer = setTimeout(() => {
      if (onCompleteRef.current) onCompleteRef.current();
    }, 1000);
    return () => clearTimeout(timer);
  }, [animationPhase]);

  if (isClaimMode) {
    return (
      <div className="container claim-mode">
        <div className="claim-shell">
          <div className="claim-card">
            <div className="claim-visual">
              <img
                src={officialLogo}
                alt="The First NonCarbon Artist"
                className="claim-logo-image"
              />
            </div>

            <div className="claim-panel">
              <div className="claim-copy">
                <h1 className="claim-title">Welcome!</h1>
              </div>

              <form onSubmit={handleClaimSubmit} className="claim-form">
                <label className="claim-label" htmlFor="claim-nickname">
                  Enter your nickname
                </label>
                <input
                  id="claim-nickname"
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="Enter a nickname"
                  maxLength={50}
                  disabled={isSubmitting}
                  className="claim-input"
                  autoFocus
                />

                {error && <div className="claim-error">{error}</div>}

                <div className="claim-session-panel">
                  <span className="claim-session-label">Session</span>
                  <span className="claim-session-id">{sessionId}</span>
                </div>

                <button type="submit" disabled={isSubmitting || !nickname.trim()} className="claim-button">
                  {isSubmitting ? "Joining..." : "Join Session"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`container ${animationDone ? "fade-out" : ""}`}>
      <div
        className="rectangle-animation-area"
        style={{ width: `${totalWidth}px`, height: `${totalHeight}px`, position: "relative" }}
      >
        {positions.map((pos, index) => (
          <div
            key={`pixel-${index}`}
            className="rectangle-pixel"
            style={{
              width: `${pixelSize}px`,
              height: `${pixelSize}px`,
              left: `${(pos.x + Math.floor(rectWidth / 2)) * pixelSize}px`,
              top: `${(pos.y + Math.floor(rectHeight / 2)) * pixelSize}px`,
            }}
          />
        ))}
        {textLines.map((line, index) => (
          <div
            key={`text-${index}`}
            className={`text-line ${textLines.length === LOADER_LINES_OF_TEXT.length ? "text-complete" : ""}`}
            style={getTextPosition(index)}
          >
            {line}
          </div>
        ))}
      </div>
      {showNickname && <Nickname onNicknameSubmit={handleNicknameSubmit} />}
    </div>
  );
};

export default Loader;
